import React, { useState } from 'react';
import StageShell from './common/StageShell';
import { getProject, setStage } from '../../services/sdlcProject.service';
import { synthesizeTests } from './sdlcSynthesizers';

const FRAMEWORKS = [
  { id: 'pytest', label: 'pytest (Python)' },
  { id: 'jest', label: 'Jest (JS)' },
  { id: 'jest-ts', label: 'Jest (TS)' },
  { id: 'junit', label: 'JUnit 5 (Java)' },
  { id: 'go-test', label: 'go test' },
];

/**
 * SDLC – Tests stage.
 *
 * Generates per-source test scaffolds from the bound code snapshot. The
 * user can pick the framework; defaults are inferred from file extensions.
 */
function TestsStage() {
  const [project, setProject] = useState(getProject());
  const code = project?.code?.snapshot;
  const tests = project?.tests;
  const [framework, setFramework] = useState(tests?.framework || null);
  const [activeFile, setActiveFile] = useState(0);

  const handleGenerate = () => {
    const result = synthesizeTests({ codeSnapshot: code, framework });
    const next = setStage('tests', result);
    setProject(next);
    setFramework(result.framework);
    setActiveFile(0);
  };

  const inputs = [
    { label: 'Architecture', value: project?.architecture?.options?.find((o) => o.id === project?.architecture?.selectedId)?.name || '' },
    { label: 'Code', value: code ? `${(code.files || []).length} files` : '' },
  ];

  const files = tests?.files || [];

  return (
    <StageShell
      stageId="tests"
      description="Generate a test scaffold for every non-test source file in the bound codebase."
      inputs={inputs}
      canContinue={files.length > 0}
      actions={<button className="stage-btn primary" onClick={handleGenerate} disabled={!code}>
        {tests ? 'Regenerate' : 'Generate tests'}
      </button>}
    >
      {!code && (
        <div className="stage-empty">
          <div className="icon">💻</div>
          No code is bound to the SDLC project yet. Bind a generated codebase in the Code stage first.
        </div>
      )}

      {code && (
        <>
          <div>
            <strong style={{ fontSize: '0.9rem' }}>Test framework</strong>
            <div className="stage-radio-row">
              {FRAMEWORKS.map((fw) => (
                <button
                  key={fw.id}
                  type="button"
                  className={`stage-radio ${(framework || tests?.framework) === fw.id ? 'active' : ''}`}
                  onClick={() => setFramework(fw.id)}
                >
                  {fw.label}
                </button>
              ))}
            </div>
          </div>

          {files.length === 0 && (
            <div className="stage-empty">
              <div className="icon">🧪</div>
              No tests generated yet. Click <em>Generate tests</em> above.
            </div>
          )}

          {files.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, marginTop: 12 }}>
              <div className="stage-file-list" style={{ marginTop: 0 }}>
                {files.map((f, i) => (
                  <button
                    key={f.path}
                    type="button"
                    className="stage-file-item"
                    style={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      background: activeFile === i ? '#eff6ff' : 'transparent',
                    }}
                    onClick={() => setActiveFile(i)}
                  >
                    <span>{f.path}</span>
                  </button>
                ))}
              </div>
              <pre className="stage-pre">{files[activeFile]?.content}</pre>
            </div>
          )}
        </>
      )}
    </StageShell>
  );
}

export default TestsStage;
